import React, { useCallback, useMemo } from "react";
import { getShouldShow } from "scmp-sdk/components/acquisition-popup-container/display/helpers";
import type {
  PopRenderPayload,
  PopupGetter,
  useRenderPopupOptions,
} from "scmp-sdk/components/acquisition-popup-container/display/types";
import { useSdkContext } from "scmp-sdk/components/contexts";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { AcquisitionNewsletterPopup } from ".";

export const useAcquisitionNewsletterPopupGetter = (
  options: useRenderPopupOptions,
): PopupGetter => {
  const {
    acquisition: { popupState },
    userCountry,
    userTypeIds,
  } = useSdkContext();

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const getNextFromConfig = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const content = Object.values(popupState.acquisitions).find(acquisition =>
        getShouldShow({
          config: acquisition,
          userInfo: {
            isLoggedIn: options.isLoggedIn,
            userCountry: userCountry ?? "",
            userDevice: isMobile ? "mobile" : "desktop",
            userTypeIds,
          },
        }),
      );
      if (!content) return;

      const props = {
        acquisition: content,
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
      };
      return {
        delay: Number.parseInt(content.timeout ?? "0", 10),
        element: <AcquisitionNewsletterPopup {...props} />,
        localStorageKey: content.localStorageKey,
        updateLocalStorageOnShow: true,
      };
    },
    [
      isMobile,
      options.isLoggedIn,
      options.modalContainerReference,
      popupState.acquisitions,
      userCountry,
      userTypeIds,
    ],
  );

  const getters = useMemo(() => ({ getNextFromConfig }), [getNextFromConfig]);

  return getters;
};
